import React, { useState, useEffect, useCallback } from 'react';
import {
  CarouselButtons,
  CarouselButton,
  CarouselContainer,
  CarouselIndexes,
  CarouselIndex,
  CarouselTrack,
  SlideContent,
  SlideDescription,
  SlideTitle,
} from './styled';
import { CarouselSlide } from './CarouselSlide';
import PrevSvg from '@icons/selfServe/prev.svg';
import NextSvg from '@icons/selfServe/next.svg';
import ResetSvg from '@icons/selfServe/reset.svg';

export interface Slide {
  id: number;
  image: string;
  title: string;
  description: string;
  type: 'image' | 'gif';
}

interface CarouselProps {
  slides: Slide[];
  indexStyle?: 'dot' | 'rectangle';
  autoPlay?: boolean;
}

export default function Carousel({ slides, autoPlay = true }: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const isLastSlide = currentIndex === slides.length - 1;

  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? slides.length - 1 : prevIndex - 1));
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const handleNext = useCallback(() => {
    if (isTransitioning) return;
    if (isLastSlide) {
      setCurrentIndex(0);
      setIsPlaying(autoPlay);
      setTimeout(() => setIsTransitioning(false), 500);
      return;
    }
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex === slides.length - 1 ? 0 : prevIndex + 1));
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, isLastSlide, slides.length, autoPlay]);

  useEffect(() => {
    let interval: number | undefined;

    if (autoPlay && isPlaying && !isLastSlide) {
      interval = window.setInterval(() => {
        handleNext();
      }, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, handleNext, isLastSlide, autoPlay]);

  const handleMouseEnter = () => {
    if (autoPlay) setIsPlaying(false);
  };

  const handleMouseLeave = () => {
    if (autoPlay) setIsPlaying(true);
  };

  return (
    <CarouselContainer onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <CarouselTrack $currentIndex={currentIndex}>
        {slides.map((slide) => (
          <CarouselSlide key={slide.id} imageUrl={slide.image} />
        ))}
      </CarouselTrack>
      <CarouselIndexes>
        <CarouselIndex>
          {currentIndex + 1} / {slides.length}
        </CarouselIndex>
        <SlideContent>
          <SlideTitle>{slides[currentIndex].title}</SlideTitle>
          <SlideDescription>{slides[currentIndex].description}</SlideDescription>
        </SlideContent>
        <CarouselButtons>
          <CarouselButton
            onClick={handlePrevious}
            disabled={isTransitioning || currentIndex === 0}
            $position="prev"
            style={{ fontSize: 24 }}
          >
            <PrevSvg strokeWidth="2" />
          </CarouselButton>
          <CarouselButton
            onClick={handleNext}
            disabled={isTransitioning}
            $position="next"
            style={{ fontSize: 24 }}
          >
            {currentIndex === slides.length - 1 ? <ResetSvg /> : <NextSvg strokeWidth="2" />}
          </CarouselButton>
        </CarouselButtons>
      </CarouselIndexes>
    </CarouselContainer>
  );
}
